import { useState, useEffect, useRef, useCallback } from "react";
import useLessonSpeech from "./useLessonSpeech";
import { requestLessonAudio } from "../services/userService";
import { useUserAuth } from "../context/UserContext.js";
import { getFreshIdToken } from "../utils/authToken.js";

/**
 * useLessonAudio
 * --------------
 * The premium narration layer promised by the "upgrade seam" in
 * {@link useLessonSpeech}. Same player interface —
 *
 *   { supported, status, play, pause, resume, stop }
 *   status ∈ "idle" | "loading" | "playing" | "paused"
 *
 * — so `ListenButton` doesn't care which engine is behind it.
 *
 * Behaviour:
 *   flag off, no lessonID, or anything at all goes wrong
 *        → the browser's speechSynthesis voice, exactly as before
 *   flag on and a clip exists
 *        → pre-generated ElevenLabs audio streamed from Cloud Storage
 *
 * Two details worth keeping:
 *
 * - **The URL is prefetched on mount, without the auth token.** The backend only
 *   generates on a cache miss for an authenticated caller, so a tokenless
 *   prefetch can never spend money — it just asks "is there a clip?". Having the
 *   URL in hand before the click matters because Safari blocks `play()` that
 *   happens after an `await` inside a click handler; with the URL prefetched,
 *   playback starts synchronously in the gesture.
 * - **Failure is always soft.** Any miss falls through to the browser voice
 *   rather than leaving the student with a dead button.
 */
// Any non-empty value turns premium narration on; the backend owns which
// provider actually synthesises, so switching provider is a server-side change
// and never needs a frontend rebuild.
const PREMIUM_TTS_ENABLED = !!process.env.REACT_APP_TTS_PROVIDER;

export default function useLessonAudio({ text, lang = "en-US", lessonID, locale = "en", rate = 1 }) {
    const browser = useLessonSpeech({ text, lang, rate });
    const { user } = useUserAuth();

    const [status, setStatus] = useState("idle");
    // Latches on once the premium path has proven unavailable for this lesson,
    // so we stop retrying and hand over to the browser voice for good.
    const [remoteOff, setRemoteOff] = useState(false);

    const urlRef = useRef(null);
    const audioRef = useRef(null);
    const tokenRef = useRef(user?.accessToken);
    tokenRef.current = user?.accessToken;
    // Read inside `attach`, which is memoised with no deps so it can't close
    // over the current rate.
    const rateRef = useRef(rate);
    rateRef.current = rate;

    const enabled = PREMIUM_TTS_ENABLED && !!lessonID && !remoteOff;

    const teardown = useCallback(() => {
        const el = audioRef.current;
        if (el) {
            el.pause();
            el.removeAttribute("src");
            el.load();
            audioRef.current = null;
        }
        setStatus("idle");
    }, []);

    // New lesson → drop the old clip and look up the new one. The prefetch is
    // tokenless on purpose (see the note above): cache hit or nothing.
    useEffect(() => {
        urlRef.current = null;
        setRemoteOff(false);
        teardown();

        if (!PREMIUM_TTS_ENABLED || !lessonID) return undefined;

        let cancelled = false;
        (async () => {
            const data = await requestLessonAudio(lessonID, locale, undefined);
            if (!cancelled && data?.url) urlRef.current = data.url;
        })();

        return () => {
            cancelled = true;
        };
    }, [lessonID, locale, teardown]);

    // Never leave a clip playing after unmount.
    useEffect(() => teardown, [teardown]);

    // Handing over to the browser voice means the <audio> element is about to
    // become unreachable — release it first so it can't keep playing with no
    // controls attached to it.
    useEffect(() => {
        if (remoteOff) teardown();
    }, [remoteOff, teardown]);

    const attach = useCallback((url) => {
        const audio = new Audio(url);
        // Pitch correction is the browser default, but narration is the one case
        // where losing it is unlistenable (2× turns the voice into a chipmunk),
        // so ask for it explicitly — including Safari's prefixed form.
        audio.preservesPitch = true;
        audio.webkitPreservesPitch = true;
        audio.playbackRate = rateRef.current;
        audio.onended = () => setStatus("idle");
        audio.onerror = () => {
            setRemoteOff(true);
            setStatus("idle");
        };
        audioRef.current = audio;
        return audio;
    }, []);

    // Speed changes apply to the clip already playing, with no interruption.
    useEffect(() => {
        if (audioRef.current) audioRef.current.playbackRate = rate;
    }, [rate]);

    const play = useCallback(() => {
        if (!enabled) return browser.play();

        // Paused mid-clip → resume in place.
        const existing = audioRef.current;
        if (existing && status === "paused") {
            existing.play().catch(() => setRemoteOff(true));
            setStatus("playing");
            return undefined;
        }

        // Normal path: URL already prefetched, so this stays inside the click
        // gesture and Safari allows it.
        if (urlRef.current) {
            const audio = existing || attach(urlRef.current);
            audio.currentTime = 0;
            audio.play().then(
                () => setStatus("playing"),
                () => {
                    setRemoteOff(true);
                    setStatus("idle");
                    browser.play();
                }
            );
            return undefined;
        }

        // No cached clip. Ask again WITH the token, which lets the backend
        // generate one. First play may be blocked by Safari's gesture rule; the
        // clip is cached by then, so the next click works — and if playback
        // fails outright we fall back to the browser voice.
        setStatus("loading");
        // Refresh rather than trusting the ref: the snapshot it holds is the one
        // taken at sign-in, so an hour in the backend rejects it and refuses to
        // generate — the student silently drops to the browser voice for good
        // (`remoteOff` latches). Falls back to the ref if the SDK has no user.
        return getFreshIdToken(tokenRef.current)
            .then((token) => requestLessonAudio(lessonID, locale, token))
            .then((data) => {
                if (!data?.url) {
                    setRemoteOff(true);
                    setStatus("idle");
                    browser.play();
                    return;
                }
                urlRef.current = data.url;
                attach(data.url)
                    .play()
                    .then(
                        () => setStatus("playing"),
                        () => setStatus("idle")
                    );
            });
    }, [enabled, status, lessonID, locale, browser, attach]);

    const pause = useCallback(() => {
        if (!enabled) return browser.pause();
        if (audioRef.current && status === "playing") {
            audioRef.current.pause();
            setStatus("paused");
        }
        return undefined;
    }, [enabled, status, browser]);

    const resume = useCallback(() => {
        if (!enabled) return browser.resume();
        if (audioRef.current && status === "paused") {
            audioRef.current.play().catch(() => setRemoteOff(true));
            setStatus("playing");
        }
        return undefined;
    }, [enabled, status, browser]);

    const stop = useCallback(() => {
        if (!enabled) return browser.stop();
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
        setStatus("idle");
        return undefined;
    }, [enabled, browser]);

    if (!enabled) return browser;

    // The premium path needs no speechSynthesis engine, so it's "supported"
    // wherever <audio> is — i.e. everywhere.
    return { supported: true, status, play, pause, resume, stop };
}
